/**
 * Fancy & Bookmaker commission — Lab `fancyAndBookmakerCommission` /
 * `account/getTotalCommissionOnDetailPage`.
 *
 * Live: Agent-API hierarchy `POST /v1/account/getTotalCommissionOnDetailPage`.
 * Demo: fixtures. Missing route → unavailable (UI keeps layout with zeros via hook).
 */
import { apiFetch } from '@/api/client'

export type FancyBookmakerCommissionQuery = {
  /** Seat whose stamped commission columns are summed (Lab `memberId`). */
  memberId: string
  /** Optional downline filter (Lab `childMemberId`); omit / `All` = every child. */
  childMemberId?: string
  from: string
  to: string
}

export type FancyBookmakerCommissionResult = {
  unavailable: boolean
  reason?: string
  asOf: string
  fancy: {
    upline: number
    downline: number
    profit: number
  }
  bookmaker: {
    upline: number
    downline: number
    profit: number
  }
}

/** Lab sails envelope from hierarchy-service. */
export type GetTotalCommissionOnDetailPageResponse = {
  value: boolean
  data?: {
    fancyUplineCommission?: number
    fancyDownlineCommission?: number
    bookmakerUplineCommission?: number
    bookmakerDownlineCommission?: number
    fancyCommAmount?: number
    bookmakerCommAmount?: number
  }
  error?: string
}

const DEMO: Omit<FancyBookmakerCommissionResult, 'asOf'> = {
  unavailable: false,
  fancy: { upline: 12_450.5, downline: 8_210.25, profit: 4_240.25 },
  bookmaker: { upline: 6_800, downline: 4_150.75, profit: 2_649.25 },
}

export function demoFancyBookmakerCommission(
  _query: FancyBookmakerCommissionQuery,
): FancyBookmakerCommissionResult {
  return { ...DEMO, asOf: new Date().toISOString() }
}

export function unavailableFancyBookmakerCommission(
  reason = 'Fancy & Bookmaker commission is not wired on Agent-API yet.',
): FancyBookmakerCommissionResult {
  return {
    unavailable: true,
    reason,
    asOf: new Date().toISOString(),
    fancy: { upline: 0, downline: 0, profit: 0 },
    bookmaker: { upline: 0, downline: 0, profit: 0 },
  }
}

export function mapLabCommissionDetail(
  data: GetTotalCommissionOnDetailPageResponse['data'] | undefined,
): FancyBookmakerCommissionResult {
  const d = data ?? {}
  return {
    unavailable: false,
    asOf: new Date().toISOString(),
    fancy: {
      upline: Number(d.fancyUplineCommission ?? 0) || 0,
      downline: Number(d.fancyDownlineCommission ?? 0) || 0,
      profit: Number(d.fancyCommAmount ?? 0) || 0,
    },
    bookmaker: {
      upline: Number(d.bookmakerUplineCommission ?? 0) || 0,
      downline: Number(d.bookmakerDownlineCommission ?? 0) || 0,
      profit: Number(d.bookmakerCommAmount ?? 0) || 0,
    },
  }
}

/**
 * Lab `Account/getTotalCommissionOnDetailPage` → Agent-API hierarchy.
 */
export async function getTotalCommissionOnDetailPage(
  query: FancyBookmakerCommissionQuery,
): Promise<GetTotalCommissionOnDetailPageResponse> {
  const out: Record<string, unknown> = {
    memberId: query.memberId,
    fromDate: query.from,
    toDate: query.to,
  }
  const child = query.childMemberId?.trim()
  if (child) out.childMemberId = child
  return apiFetch<GetTotalCommissionOnDetailPageResponse>(
    '/v1/account/getTotalCommissionOnDetailPage',
    {
      method: 'POST',
      body: JSON.stringify(out),
    },
  )
}
